import { AccountCircleIcon, PlusOutlineIcon } from '@carboncopyco/icons';
import type { UserPersonas } from '@carboncopyco/types/desktop';
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenuItem,
  Separator,
  TextBase,
  TextBoxEditorTitle,
  TextLargeParagraph,
  ToggleButton,
  TooltipBubble,
} from '@carboncopyco/ui';
import { useEffect, useState } from 'react';
import { getInitials } from '../../../util/util';
import { useFocusAppService } from '../../hooks/useFocusAppService';
import { useTabManager } from '../../services/TabManager';
import { assignColor } from '../../shared/utils';
import { useTabs } from '../../store/context';
import { ManagePersonasModal } from './ManagePersonasModal';
import { PersonaAddNewModal } from './PersonaAddNewModal';
import { getPersonasIdDefaultOnTop } from './util';

interface PersonaHeaderProps {
  personasData: UserPersonas;
  onSwitchActivePersona: (personaId: string) => void;
  isLoaded: boolean;
  isReadOnly?: boolean;
}

export const PersonaHeader = ({
  personasData,
  onSwitchActivePersona,
  isLoaded,
  isReadOnly = false,
}: PersonaHeaderProps) => {
  const { triggerFocus } = useFocusAppService();
  const { resetPersona, deletePersona } = useTabManager();
  const { activeGlobalTabId } = useTabs();

  const [activePersonaId, setActivePersonaId] = useState<string>('');
  const [editPersonaId, setEditPersonaId] = useState<string>('');
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const [isAddNewPersonaModalOpen, setIsAddNewPersonaModalOpen] =
    useState(false);

  const [isManagePersonasModalOpen, setIsManagePersonasModalOpen] =
    useState(false);

  useEffect(() => {
    if (activePersonaId === '') {
      setActivePersonaId(personasData.defaultId);
    }
  }, [activePersonaId]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [activeGlobalTabId]);

  const activePersona = personasData?.personas[activePersonaId];

  const selectPersona = (personaId: string) => {
    setActivePersonaId(personaId);
    onSwitchActivePersona(personaId);
  };

  const handleEditPersona = (personaId: string) => {
    setIsAddNewPersonaModalOpen(true);
    setEditPersonaId(personaId);
  };

  const handleResetPersona = (personaId: string) => {
    resetPersona(personaId);
    setIsManagePersonasModalOpen(false);
    triggerFocus(false);
  };

  const handleDeletePersona = (personaId: string) => {
    deletePersona(personaId);
  };

  if (!Object.keys(personasData.personas).includes(activePersonaId)) {
    setActivePersonaId(personasData.defaultId);
  }

  return activePersona?.name ? (
    <div>
      <Dropdown
        align="end"
        open={isMenuOpen}
        onOpenChange={(isOpen) => {
          triggerFocus(isOpen);
          setIsMenuOpen(isOpen);
        }}
        disabled={!isLoaded}
        interactive
        onInteractOutside={() => setIsMenuOpen(false)}
        triggerElementAriaLabel="Change persona"
        triggerElement={
          <div className="flex justify-end">
            <TooltipBubble
              placement="left"
              title={activePersona.name}
            >
              <ToggleButton
                disabled={false}
                dark
                selected={false}
                icon={
                  <div>
                    {activePersona.isGuest ? (
                      <AccountCircleIcon color="#9B9AA2" />
                    ) : (
                      <Avatar
                        initials={getInitials(activePersona.name)}
                        size="small"
                        backgroundColor={assignColor(activePersona.name)}
                      />
                    )}
                  </div>
                }
              />
            </TooltipBubble>
          </div>
        }
      >
        <div className="w-ds-308 h-ds-308">
          <DropdownMenuItem>
            <div className="px-3 pb-3 pt-5 flex items-center">
              {activePersona.isGuest ? (
                <AccountCircleIcon
                  color="#9B9AA2"
                  size={48}
                />
              ) : (
                <Avatar
                  initials={getInitials(activePersona.name)}
                  size="medium"
                  backgroundColor={assignColor(activePersona.name)}
                />
              )}
              <div className="pl-3 flex flex-col">
                <TooltipBubble
                  placement="top"
                  title={activePersona.name}
                >
                  <div className="flex items-center w-60">
                    <TextLargeParagraph
                      label={activePersona.name}
                      color="text-ds-natural-700"
                      ellipsis
                    />
                    <div className="pl-1">
                      {personasData.defaultId === activePersonaId && (
                        <Badge
                          label="DEFAULT"
                          variant="grey"
                        />
                      )}
                    </div>
                  </div>
                </TooltipBubble>

                <TextBase
                  label={activePersona.description}
                  color="text-ds-natural-300"
                />
              </div>
            </div>
          </DropdownMenuItem>
          {!isReadOnly && (
            <>
              <div className="py-2">
                <Separator horizontal />
              </div>

              <DropdownMenuItem>
                <div className="py-1 px-3 flex items-center justify-between">
                  <TextBoxEditorTitle
                    label="OTHER PROFILES"
                    color="text-ds-natural-300"
                  />
                  <Button
                    link
                    blue
                    label="Manage"
                    onClick={() => {
                      setIsManagePersonasModalOpen(true);
                      setIsMenuOpen(false);
                    }}
                  />
                </div>
              </DropdownMenuItem>
              <div className="overflow-auto max-h-60">
                {getPersonasIdDefaultOnTop(personasData)
                  .filter((profileId) => profileId !== activePersonaId)
                  .map((personaId) => (
                    <DropdownItem
                      onClick={() => selectPersona(personaId)}
                      key={personaId}
                    >
                      <div className="py-2 flex items-center ">
                        {personasData.personas[personaId].isGuest ? (
                          <div className="px-2.5">
                            <AccountCircleIcon
                              size={29}
                              color="#9B9AA2"
                            />
                          </div>
                        ) : (
                          <div className="px-3">
                            <Avatar
                              initials={getInitials(
                                personasData.personas[personaId].name
                              )}
                              size="small"
                              backgroundColor={assignColor(
                                personasData.personas[personaId].name
                              )}
                            />
                          </div>
                        )}
                        <TooltipBubble
                          placement="top-start"
                          title={personasData.personas[personaId].name}
                        >
                          <div className=" flex items-center w-52">
                            <TextBase
                              label={personasData.personas[personaId].name}
                              color="text-ds-natural-700"
                              ellipsis
                            />
                            <div className="pl-1">
                              {personasData.defaultId === personaId && (
                                <Badge
                                  label="DEFAULT"
                                  variant="grey"
                                />
                              )}
                            </div>
                          </div>
                        </TooltipBubble>
                      </div>
                    </DropdownItem>
                  ))}
              </div>
              <Button
                link
                blue
                leftIcon={<PlusOutlineIcon />}
                label="Add"
                onClick={() => {
                  setIsAddNewPersonaModalOpen(true);
                  setIsMenuOpen(false);
                }}
              />
            </>
          )}
        </div>
      </Dropdown>

      {isAddNewPersonaModalOpen && (
        <PersonaAddNewModal
          open
          persona={{
            ...personasData.personas[editPersonaId],
            id: editPersonaId,
            isDefault: personasData.defaultId === editPersonaId,
          }}
          onClose={() => {
            setIsAddNewPersonaModalOpen(false);
            setEditPersonaId('');
          }}
        />
      )}
      {isManagePersonasModalOpen && (
        <ManagePersonasModal
          personasData={personasData}
          open
          onClose={() => {
            setIsManagePersonasModalOpen(false);
            setEditPersonaId('');
          }}
          onAddNewPersona={() => {
            setIsAddNewPersonaModalOpen(true);
          }}
          onEditPersona={handleEditPersona}
          onResetPersona={handleResetPersona}
          onDeletePersona={handleDeletePersona}
        />
      )}
    </div>
  ) : null;
};
